/**
 * VIN (Vehicle Identification Number) — normalization and validation.
 *
 * Customer-provided only. A VIN is never extracted evidence: it is stored with
 * Customer Provided lineage and never overwrites an OCR fact.
 *
 * Canonical rules (ISO 3779): 17 characters, letters and digits only, and the
 * letters I, O and Q are never used.
 */

/** Uppercase, strip everything that is not a letter/digit, cap at 17. */
export function normalizeVin(raw: string | null | undefined): string {
  if (typeof raw !== "string") return "";
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 17);
}

/** Trim to NULL when blank — a missing VIN is never an empty string. */
export function vinOrNull(raw: string | null | undefined): string | null {
  const v = normalizeVin(raw);
  return v.length > 0 ? v : null;
}

/** True when the value contains one of the forbidden characters. */
export function hasForbiddenVinChars(value: string): boolean {
  return /[IOQ]/.test(normalizeVin(value));
}

/**
 * Inline validation message, or null when acceptable.
 *
 * `attempted` gates the "incomplete" message: a VIN that is simply still being
 * typed is never an error until the customer tries to continue or leaves the
 * field. Forbidden characters are always an immediate error.
 */
export function vinValidationError(
  raw: string | null | undefined,
  attempted = false,
): string | null {
  const v = normalizeVin(raw);
  if (v.length === 0) return null; // Optional — blank never blocks.
  if (hasForbiddenVinChars(v))
    return "A VIN never contains the letters I, O, or Q. Please re-check the VIN.";
  if (attempted && v.length < 17) return "Enter all 17 characters of the VIN.";
  return null;
}

/** Last 6 characters of the VIN, or "" when unavailable. */
export function vinLast6(raw: string | null | undefined): string {
  const v = normalizeVin(raw);
  return v.length >= 6 ? v.slice(-6) : v;
}

/** Case-insensitive, formatting-insensitive VIN search match. */
export function vinMatches(vin: string | null | undefined, needle: string): boolean {
  const n = normalizeVin(needle);
  if (!n) return true;
  const v = normalizeVin(vin);
  if (!v) return false;
  return v.includes(n);
}
